// Lib
import { createSelector } from 'reselect';
import { intersection, includes, concat } from 'lodash';

// Util
import { createShallowSelector } from '../milanoteReselect/milanoteReselect';
import { getPersonalUserChannel } from '../../../common/utils/socket/socketChannelUtil';

// Selectors
import { getTrackedUserIds, getRemoteActiveCurrentlyViewedBoardIds } from './remoteUsersSelector';
import { getCurrentUserId, isGuestSelector, getCurrentUserQuickNotesRootBoardId } from '../../user/currentUserSelector';
import { breadcrumbSelector } from '../../workspace/breadcrumbs/breadcrumbSelector';
import popupOpenSelector from '../../components/popupPanel/popupOpenSelector';
import { getCurrentBoardVisiblePhysicalDescendantIds } from '../../element/selectors/currentBoardSelector';

// Constants
import { LIVE_ACTIVITY_SUFFIX } from '../../../common/utils/socket/socketConstants';
import { PopupIds } from '../../components/popupPanel/popupConstants';

// A threshold for the number of connected boards before previously connected boards will be dropped
const CONNECTED_BOARD_THRESHOLD = 10;
const EMPTY_ARRAY = [];

/**
 * Gets the channels that are stored within the Redux store.
 */
export const storedBoardChannelsSelector = (state) => state.getIn(['app', 'socketConnection', 'boardChannels']);

/**
 * Viewed ancestors & viewed deep children (not descendants, just children of the current board)
 *  = active board channels we want to connect to.
 */
const activeBoardChannelSelector = createShallowSelector(
    getRemoteActiveCurrentlyViewedBoardIds,
    breadcrumbSelector,
    getCurrentBoardVisiblePhysicalDescendantIds,
    isGuestSelector,
    (currentlyViewedBoardIds, breadcrumbObj, visibleChildPhysicalIds, isGuest) => {
        if (isGuest) return EMPTY_ARRAY;

        // Includes the current board
        const viewableAncestors = breadcrumbObj.path;
        const viewedAncestors = intersection(viewableAncestors, currentlyViewedBoardIds);
        const viewedChildren = intersection(visibleChildPhysicalIds, currentlyViewedBoardIds);

        return [...viewedAncestors, ...viewedChildren];
    },
);

/**
 * Active board channels (Y) + previously active board channels (X)
 *    (where: X = 5 - Y  iff Y < 5)
 */
export const connectedBoardChannelSelector = createShallowSelector(
    activeBoardChannelSelector,
    storedBoardChannelsSelector,
    isGuestSelector,
    (activeBoardChannels, currentlyConnectedChannels, isGuest) => {
        if (isGuest) return EMPTY_ARRAY;

        // Don't connect to previously connected boards if there are more than 10 active boards
        if (activeBoardChannels.length > CONNECTED_BOARD_THRESHOLD) return activeBoardChannels;

        const availablePreviouslyConnectedSpaces = CONNECTED_BOARD_THRESHOLD - activeBoardChannels.length;

        const previouslyConnectedChannels = currentlyConnectedChannels
            .filter((channel) => !includes(activeBoardChannels, channel))
            .take(availablePreviouslyConnectedSpaces)
            .toJS();

        return concat(activeBoardChannels, previouslyConnectedChannels);
    },
);

const getQuickNotesChannels = createShallowSelector(
    getCurrentUserQuickNotesRootBoardId,
    popupOpenSelector(PopupIds.QUICK_NOTES),
    (quickNotesRootId, quickNotesPopupOpen) => {
        const channels = [quickNotesRootId];

        if (!quickNotesPopupOpen) return channels;

        channels.push(`${quickNotesRootId}${LIVE_ACTIVITY_SUFFIX}`);

        return channels;
    },
);

/**
 * Gets the channels based on the currently active viewed boards, the user's breadcrumb as well as a 'live activity'
 * channel which will simply be the current board ID with a '-LIVE' suffix.  The latter channel is used for sending
 * selection and deselection messages.
 */
// This selector will always need to be a different value if it's invoked, so there's no point using the
// smart selector here
const channelSelector = createSelector(
    getTrackedUserIds,
    connectedBoardChannelSelector,
    (state, ownProps) => ownProps.currentBoardId,
    isGuestSelector,
    getCurrentUserId,
    getQuickNotesChannels,
    (userIds, connectedBoardChannels, currentBoardId, isGuest, currentUserId, quickNotesChannels) => {
        if (isGuest) return EMPTY_ARRAY;

        const channels = [
            ...connectedBoardChannels,
            ...userIds,
            // TODO Potential improvement - don't join this channel when on a personal board (also no need to sync
            // TODO    selection and deselection actions in that instance).
            `${currentBoardId}${LIVE_ACTIVITY_SUFFIX}`,
            // The personal channel for the current user. This is used for socket communication between the server
            // and this specific user (such as notifications etc)
            getPersonalUserChannel(currentUserId),
            ...quickNotesChannels,
        ];

        return channels;
    },
);

// This selector will always need to be a different value if it's invoked, so there's no point using the
// smart selector here
export default createSelector(
    channelSelector,
    connectedBoardChannelSelector,
    isGuestSelector,
    (channels, boardChannels, isGuest) => ({ channels, boardChannels, isGuest }),
);
